import { useMemo } from 'react';
import styled from 'styled-components';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { EconRun } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { terracotta } from '@shared/theme/tokens';
import { ECON_MODULES, fmtPeriodShort, sortRunsByPeriodAsc } from '@entities/economic/types';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import { useCountUp } from './econAnimations';
import { ObjectEconomyCard } from './ObjectEconomyCard';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const StatCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(4)};
`;

const StatLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ChartCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing(5)};
`;

const ChartTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: 16px;
  font-weight: 600;
`;

const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: ${({ theme }) => theme.spacing(2)} 0;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const Swatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: ${({ theme }) => theme.radius.xs};
  background: ${({ $color }) => $color};
`;

/** Модули с временным рядом на графике динамики. `exec_pack` (М5) — мета-модуль
 *  (агрегат KPI из готовых прогонов, а не собственный поток обработок), поэтому
 *  своей серии на графике не имеет и в счётчик динамики не попадает. */
const CHART_MODULE_IDS = ['bdr', 'mining', 'canteen', 'cashflow'] as const;
type ChartModuleId = (typeof CHART_MODULE_IDS)[number];

function isChartModule(m: string): m is ChartModuleId {
  return (CHART_MODULE_IDS as readonly string[]).includes(m);
}

/** Фирменные оттенки модулей для разнесения серий на графике. */
const MODULE_COLOR: Record<ChartModuleId, string> = {
  bdr: terracotta.clay,
  mining: '#1976D2',
  canteen: '#F57C00',
  cashflow: '#2E7D32',
};

/** Анимированное число-счётчик. Выделен в компонент, т.к. хук нельзя звать
 *  в цикле/условии — каждая карточка-метрика монтирует свой. */
function CountUpValue({ value }: { value: number }) {
  const animated = useCountUp(value);
  return <StatValue>{Math.round(animated).toLocaleString('ru-RU')}</StatValue>;
}

interface ChartPoint {
  period: string;
  bdr: number;
  mining: number;
  canteen: number;
  cashflow: number;
}

export function EconDashboard({ runs }: { runs: EconRun[] }) {
  const { activeProjectName } = useActiveProjectContext();
  const stats = useMemo(() => {
    const total = runs.length;
    const completed = runs.filter((r) => r.status === 'completed').length;
    const active = runs.filter((r) => r.status === 'pending' || r.status === 'processing').length;
    return { total, completed, active };
  }, [runs]);

  // Динамика по периодам: сколько обработок каждого модуля в каждом месяце.
  const chartData = useMemo<ChartPoint[]>(() => {
    const byPeriod = new Map<string, ChartPoint>();
    for (const r of sortRunsByPeriodAsc(runs)) {
      const key = fmtPeriodShort(r.period);
      const point =
        byPeriod.get(key) ??
        ({ period: key, bdr: 0, mining: 0, canteen: 0, cashflow: 0 } satisfies ChartPoint);
      if (isChartModule(r.module)) point[r.module] += 1;
      byPeriod.set(key, point);
    }
    return Array.from(byPeriod.values());
  }, [runs]);

  const hasSeries = chartData.length > 0;
  const latestScopedObject = useMemo(
    () =>
      runs.find((r) => r.object_scope && r.object_scope.trim())?.object_scope?.trim() ||
      activeProjectName ||
      undefined,
    [runs, activeProjectName],
  );

  return (
    <Wrap>
      <StatsRow>
        <StatCard $delay={0}>
          <StatLabel>Обработок всего</StatLabel>
          <CountUpValue value={stats.total} />
        </StatCard>
        <StatCard $delay={60}>
          <StatLabel>Завершено</StatLabel>
          <CountUpValue value={stats.completed} />
        </StatCard>
        <StatCard $delay={120}>
          <StatLabel>В работе</StatLabel>
          <CountUpValue value={stats.active} />
        </StatCard>
      </StatsRow>

      {latestScopedObject && <ObjectEconomyCard objectName={latestScopedObject} />}

      <ChartCard $delay={160}>
        <ChartTitle>Динамика обработок по периодам</ChartTitle>
        {hasSeries ? (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  {(Object.keys(MODULE_COLOR) as ChartModuleId[]).map((m) => (
                    <linearGradient key={m} id={`econ-grad-${m}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={MODULE_COLOR[m]} stopOpacity={0.38} />
                      <stop offset="100%" stopColor={MODULE_COLOR[m]} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,128,0.15)" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="rgba(120,120,128,0.5)" />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11 }}
                  stroke="rgba(120,120,128,0.5)"
                />
                <Tooltip
                  contentStyle={{
                    background: 'rgba(20,20,24,0.95)',
                    border: '1px solid rgba(120,120,128,0.3)',
                    borderRadius: 8,
                    fontSize: 12,
                    color: '#fff',
                  }}
                />
                {(Object.keys(MODULE_COLOR) as ChartModuleId[]).map((m) => (
                  <Area
                    key={m}
                    type="monotone"
                    dataKey={m}
                    name={ECON_MODULES[m].short}
                    stroke={MODULE_COLOR[m]}
                    strokeWidth={2}
                    fill={`url(#econ-grad-${m})`}
                    animationDuration={500}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
            <Legend>
              {(Object.keys(MODULE_COLOR) as ChartModuleId[]).map((m) => (
                <LegendItem key={m}>
                  <Swatch $color={MODULE_COLOR[m]} />
                  {ECON_MODULES[m].short}
                </LegendItem>
              ))}
            </Legend>
          </>
        ) : (
          <Empty>Пока нет завершённых обработок — динамика появится после первых расчётов.</Empty>
        )}
      </ChartCard>
    </Wrap>
  );
}
